import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const SESSION_ID = "web-" + Math.random().toString(36).slice(2);

const SUGGESTIONS = [
  "What AI services do you offer?",
  "Tell me about your RAG work",
  "How do I start a project?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Grow, the AI assistant for Growing IT Solutions. Ask me anything about our AI services, case studies or how we can help your business." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: SESSION_ID, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: ?/, "");
          if (line === "[DONE]") continue;
          if (line) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + line };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please email contact@growingitsolutions.com." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-white grid place-items-center glow-blue hover:scale-105 transition-transform"
        aria-label="Chat with us"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] glass border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-[#0F111A]">
            <div className="w-9 h-9 rounded-full bg-primary/20 grid place-items-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">Grow — AI Assistant</p>
              <p className="text-xs text-emerald-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> Online
              </p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                    m.role === "user"
                      ? "bg-primary text-white rounded-br-sm"
                      : "bg-[#1E293B] text-slate-100 rounded-bl-sm"
                  }`}
                >
                  {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={150} /><Dot d={300} /></span>}
                </div>
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 border border-white/10 text-slate-300 hover:border-primary hover:text-primary transition-colors rounded-full"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-white/10 flex items-center gap-2 bg-[#0F111A]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our AI services..."
              className="flex-1 bg-[#05060A] border border-white/10 focus:border-primary outline-none text-sm text-white px-3.5 py-2.5 rounded-lg transition-colors"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={busy}
              className="w-10 h-10 grid place-items-center bg-primary text-white rounded-lg disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

const Dot = ({ d = 0 }) => (
  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 pulse-dot" style={{ animationDelay: `${d}ms` }} />
);
